import type { AxiosInstance, AxiosResponse } from 'axios';
import axios from 'axios';
import i18n from '../../../../i18n';
import type { IamPasswordValidationError } from '../../../enum';
import type {
    IIamCheckOtpParams,
    IIamCheckPasswordPolicyParams,
    IIamIdentifyUserParams,
    IIamPasswordResetResponse,
    IIamResetPasswordParams,
} from '../../domain';
import { SettingsApiService } from '../settings/settings-api-service';

export enum IAMEndpoint {
    IDENTIFY_USER = 'password-reset/username/identify/',
    CHECK_PASSWORD_POLICY = 'password/policy/check/',
    CHECK_OTP = 'password-reset/verification/email/otp/check/',
    RESET_PASSWORD = 'password-reset/password/',
    CANCEL_PASSWORD_RESET = 'password-reset/flow/',
}

export class IamApiService {
    protected http: AxiosInstance;

    constructor(context?: string) {
        this.http = axios.create({
            baseURL: context,
        });
        this.getBaseUrl().then((baseURL: string) => {
            this.http = axios.create({
                baseURL,
                headers: {
                    'X-Same-Domain': 1,
                    'Accept-Language': i18n.language,
                },
                withCredentials: true,
            });
        });
    }

    async identifyUser(params: IIamIdentifyUserParams): Promise<IIamPasswordResetResponse> {
        return this.http.post(IAMEndpoint.IDENTIFY_USER, params).then(({ data }: AxiosResponse) => data);
    }

    async checkOtp(params: IIamCheckOtpParams): Promise<IIamPasswordResetResponse> {
        return this.http.post(IAMEndpoint.CHECK_OTP, params).then(({ data }: AxiosResponse) => data);
    }

    async checkPasswordPolicy(params: IIamCheckPasswordPolicyParams): Promise<null | IamPasswordValidationError> {
        return this.http
            .post(IAMEndpoint.CHECK_PASSWORD_POLICY, params)
            .then(({ data }: AxiosResponse) => data)
            .catch((error) => error.response.data.errors[0].meta.detail);
    }

    async resetPassword(params: IIamResetPasswordParams): Promise<IIamPasswordResetResponse> {
        return this.http.post(IAMEndpoint.RESET_PASSWORD, params).then(({ data }: AxiosResponse) => data);
    }

    async cancelPasswordReset() {
        return this.http.delete(IAMEndpoint.CANCEL_PASSWORD_RESET).then(({ data }: AxiosResponse) => data);
    }

    private async getBaseUrl() {
        const headers = await new SettingsApiService().getContextHeaders();

        return headers['x-sec-iam-api-url'];
    }
}
